/**
 * A book's chapters in the local store (decisions 13, 62, 68; sync case table L4, L6, L7 and the local-trash rule;
 * part 08 log, principles 1–6, tables B and X). Every operation runs in the snapshot history's queue and re-reads what
 * it acts on there; each writes everything it changes in one write through the edit session.
 */
import { orderChapters, parseChapter, place, SplitError, splitChapter, toHex } from '@cockatrice/core';
import type { Piece } from '@cockatrice/core';
import type { SessionWriteResult } from './edit-lock.ts';
import type { SnapshotHistory } from './history.ts';
import { commentNote } from './legacy-notes.ts';
import { StoreRecordError } from './records.ts';
import type { BookRecord, ChapterEntry, ChapterText, CommentRecord, ConflictCopy, NoteRecord, Snapshot } from './records.ts';
import type { InvalidRecord, Store, StoreFailure, WriteOp } from './store.types.ts';

/** How many fresh IDs a new chapter tries before giving up (B2). */
export const MAX_ID_TRIES = 8;

/** What {@link ChapterBook} needs; everything with an effect is injected. */
export interface ChapterBookOptions {
  readonly store: Store;
  /** The edit session's write (part 06): committed only while this tab holds the lock. */
  readonly write: (ops: readonly WriteOp[]) => Promise<SessionWriteResult>;
  /** This device's snapshot history; book operations run in its queue (principle 6). */
  readonly history: SnapshotHistory;
  /** This device's ID (32 lowercase hex digits), recorded as where a chapter was deleted (B7). */
  readonly device: string;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
  /** A fresh ID. */
  readonly newId: () => string;
  /** SHA-256 (WebCrypto's digest): a re-imported comment's record ID (part 23c, table DD). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
}

/** A book as the writer sees it (principle 2, B16). */
export interface BookListing {
  /** The chapters in the book, in order (principle 3). */
  readonly chapters: readonly ChapterEntry[];
  /** The chapters in the trash, the most recently deleted first. */
  readonly trash: readonly ChapterEntry[];
  /** Conflict copies of the book whose chapter has no entry (X4): typing kept after the chapter was deleted forever. */
  readonly orphanCopies: readonly ConflictCopy[];
  /** Entries and conflict copies of the book the reader refused, by key (part 06, R4). */
  readonly invalid: readonly InvalidRecord[];
}

/** Why an operation found nothing to do: no such chapter (or `before` isn't one), or it is in the trash (B3, B5). */
type Missing = { readonly kind: 'missing' } | { readonly kind: 'trashed' };

/** Not written: this tab lost the lock, or the store failed (B17, B18). */
type NotWritten = { readonly kind: 'stale' } | StoreFailure;

/** The outcome of {@link ChapterBook.create}. */
export type CreateChapterResult = { readonly kind: 'created'; readonly entry: ChapterEntry } | { readonly kind: 'missing' } | NotWritten;

/**
 * The outcome of {@link ChapterBook.createBook} (part 18, V10): the new chapters in order; `exists` when the book's ID
 * is taken (a record or a chapter already names it).
 */
export type CreateBookResult = { readonly kind: 'created'; readonly chapters: readonly ChapterEntry[] } | { readonly kind: 'exists' } | NotWritten;

/** The outcome of a change to a chapter in the book: {@link ChapterBook.move}, `update`, `trash`. */
export type ChangeResult = { readonly kind: 'done' } | Missing | NotWritten;

/** The outcome of {@link ChapterBook.restore} and {@link ChapterBook.deleteForever}: `live` when it isn't in the trash. */
export type TrashResult = { readonly kind: 'done' } | { readonly kind: 'live' } | { readonly kind: 'missing' } | NotWritten;

/** The outcome of {@link ChapterBook.restoreSnapshot} (B12, B13). */
export type RestoreSnapshotResult =
  | { readonly kind: 'restored'; readonly text: string; readonly before: Snapshot }
  | Missing
  | NotWritten;

/** The outcome of {@link ChapterBook.split} (B14, B15): the new chapters in order, or the error that refused it. */
export type SplitResult =
  | { readonly kind: 'split'; readonly chapters: readonly ChapterEntry[] }
  | { readonly kind: 'refused'; readonly error: SplitError }
  | Missing
  | NotWritten;

/** One chapter a replace-all changes (part 19a, L3): its text as the replace read it, and as it wrote it. */
export interface ReplacedText {
  readonly id: string;
  readonly bookId: string;
  readonly before: string;
  readonly after: string;
}

/**
 * The outcome of {@link ChapterBook.replaceAll} (part 19a, table L): every text written, or none: `changed` when a
 * chapter's stored text is no longer the one replaced, or it went or is in the trash (L7).
 */
export type ReplaceAllResult = { readonly kind: 'replaced' } | { readonly kind: 'changed' } | NotWritten;

/**
 * The outcome of {@link ChapterBook.undoReplaceAll} (part 19a, table U): the chapters given their earlier text back
 * (U1, U4) and those left (U2, U3), by ID in the order given; or nothing written (U5).
 */
export type UndoReplaceAllResult = { readonly kind: 'undone'; readonly restored: readonly string[]; readonly kept: readonly string[] } | NotWritten;

/**
 * A comment a re-import stores (part 23c, table AC), as a Word note since part 19e (decision 76): its comment's fields but
 * its ID, book, chapter and when it is stored; the ID is made from the book, the chapter and the comment's key (table DD,
 * deep-fix r3).
 */
export type CommentDraft = Omit<CommentRecord, 'id' | 'bookId' | 'chapterId' | 'storedAt'>;

/** DD (deep-fix r3): what a comment's record ID is made from, first naming what it is and its rule's version. */
const COMMENT_ID_KIND = 'cockatrice-comment-id';

/**
 * Part 23c, table DD (deep-fix r3): the record ID of the comment keyed `sourceKey` on chapter `chapterId` of book `bookId`:
 * the first 32 hex digits of the SHA-256 (`digest`) of `["cockatrice-comment-id",2,book,chapter,sourceKey]` as JSON. A
 * re-import makes it for each Word comment; a book imported as a copy makes its comments' again (part 24c, P10).
 */
export async function commentId(digest: (bytes: Uint8Array) => Promise<Uint8Array>, bookId: string, chapterId: string, sourceKey: string): Promise<string> {
  return toHex(await digest(new TextEncoder().encode(JSON.stringify([COMMENT_ID_KIND, 2, bookId, chapterId, sourceKey])))).slice(0, 32);
}

/**
 * One chapter a re-import takes (part 23b, table AP): its stored text as the review read it (`before`) and as the merge
 * made it (`after`), its new title (null: kept, CS8) and its title as reviewed, and whether `before` is kept as a conflict
 * copy (CS4, CS5); and the Word comments anchored in `after` (part 23c).
 */
export interface ReimportedText {
  readonly id: string;
  readonly before: string;
  readonly after: string;
  readonly title: string | null;
  /** The chapter's title as reviewed: a new `title` is written only while the stored one is still it (AP2, deep-fix r4). */
  readonly titleBefore: string;
  readonly copy: boolean;
  readonly comments?: readonly CommentDraft[];
}

/**
 * One chapter a re-import adds (CS7): its title and text, after the live chapter `after` (null: first), in file order;
 * and the Word comments anchored in its text (part 23c).
 */
export interface ReimportedDraft {
  readonly title: string;
  readonly text: string;
  readonly after: string | null;
  readonly comments?: readonly CommentDraft[];
}

/** A chapter a re-import doesn't change that holds Word comments (part 23c, AC4): its stored text as they were anchored in. */
export interface CommentedChapter {
  readonly id: string;
  readonly text: string;
  readonly comments: readonly CommentDraft[];
}

/**
 * The outcome of {@link ChapterBook.reimport} (table AP): everything written, with the chapters added in order and how
 * many comment records were new (AC1, AC2); or none: `changed` when a taken chapter's stored text isn't the one reviewed,
 * or it, an anchor or a commented chapter is no longer live, or a commented chapter's text isn't the one anchored in
 * (CS11, AC4).
 */
export type ReimportResult = { readonly kind: 'reimported'; readonly added: readonly ChapterEntry[]; readonly comments: number } | { readonly kind: 'changed' } | NotWritten;

/** The fields of an entry the writer edits (B6). */
export interface EntryFields {
  readonly title?: string;
  readonly synopsis?: string;
}

/** A write's result, with `committed` turned into `done`. */
function done(result: SessionWriteResult): { readonly kind: 'done' } | NotWritten {
  return result.kind === 'committed' ? { kind: 'done' } : result;
}

/** The ops that store each neighbour {@link place} moved. */
function movedOps(moved: readonly (readonly [ChapterEntry, string])[]): WriteOp[] {
  return moved.map(([entry, position]): WriteOp => ({ kind: 'put', store: 'entries', value: { ...entry, position } }));
}

/** The ops that store new chapters: each entry and its text. */
function newOps(chapters: readonly (readonly [ChapterEntry, string])[]): WriteOp[] {
  return chapters.flatMap(([entry, text]): WriteOp[] => [
    { kind: 'put', store: 'entries', value: entry },
    { kind: 'put', store: 'chapters', value: { id: entry.id, bookId: entry.bookId, text } },
  ]);
}

export class ChapterBook {
  private readonly options: ChapterBookOptions;

  constructor(options: ChapterBookOptions) {
    this.options = options;
  }

  /** The book's chapters, trash, orphan copies and refused records (principle 2, B16). */
  list(bookId: string): Promise<BookListing | StoreFailure> {
    return this.options.history.exclusive(async () => {
      const entries = await this.options.store.readWhere('entries', 'bookId', bookId);
      const copies = await this.options.store.readWhere('conflictCopies', 'bookId', bookId);
      const known = new Set<unknown>([...entries.records.map((entry) => entry.id), ...entries.invalid.map((record) => record.key)]);
      return {
        chapters: orderChapters(entries.records.filter((entry) => entry.trashed === null)),
        // Records come in key order and the sort is stable, so deletions at the same time go by ID.
        trash: entries.records
          .flatMap((entry) => (entry.trashed === null ? [] : [{ entry, at: entry.trashed.at }]))
          .sort((a, b) => b.at - a.at)
          .map(({ entry }) => entry),
        orphanCopies: copies.records.filter((copy) => !known.has(copy.chapterId)),
        invalid: [...entries.invalid, ...copies.invalid],
      };
    });
  }

  /**
   * A new chapter before the live chapter `before`, or at the end for null (B1–B3), with `text` (empty by default) and
   * `title`, entry and text in one write.
   */
  create(bookId: string, before: string | null, fields: { readonly title?: string; readonly text?: string } = {}): Promise<CreateChapterResult> {
    return this.options.history.exclusive(async (): Promise<CreateChapterResult> => {
      const live = await this.live(bookId);
      const slot = before === null ? live.length : live.findIndex((entry) => entry.id === before);
      if (slot < 0) return { kind: 'missing' };
      // With no free ID there is nothing to place, and the loop below doesn't run (B2).
      const { placed, moved } = place(live, slot, (await this.withIds([fields])) ?? []);
      for (const [[draft, id], position] of placed) {
        const entry: ChapterEntry = { id, bookId, title: draft.title ?? '', position, synopsis: '', trashed: null };
        const written = await this.options.write([...newOps([[entry, draft.text ?? '']]), ...movedOps(moved)]);
        return written.kind === 'committed' ? { kind: 'created', entry } : written;
      }
      return { kind: 'failed', error: new Error('no free chapter ID') };
    });
  }

  /**
   * Part 18, V10 (principle 5): a new book: its record, and a chapter for each of `drafts` in order, with its title and
   * text, all in one write, so an import is stored whole or not at all. A book whose ID a record or an entry already has
   * is never touched (`exists`).
   */
  createBook(book: BookRecord, drafts: readonly { readonly title: string; readonly text: string }[]): Promise<CreateBookResult> {
    return this.options.history.exclusive(async (): Promise<CreateBookResult> => {
      const { store } = this.options;
      if ((await store.read('books', book.id)) !== undefined || (await store.readWhere('entries', 'bookId', book.id)).records.length > 0) return { kind: 'exists' };
      const paired = await this.withIds(drafts);
      if (paired === null) return { kind: 'failed', error: new Error('no free chapter ID') };
      const chapters = place([], 0, paired).placed.map(([[draft, id], position]): readonly [ChapterEntry, string] => [
        { id, bookId: book.id, title: draft.title, position, synopsis: '', trashed: null },
        draft.text,
      ]);
      const written = await this.options.write([{ kind: 'put', store: 'books', value: book }, ...newOps(chapters)]);
      return written.kind === 'committed' ? { kind: 'created', chapters: chapters.map(([entry]) => entry) } : written;
    });
  }

  /** Moves a live chapter before the live chapter `before`, or to the end for null (B3–B5, O8). */
  move(chapterId: string, before: string | null): Promise<ChangeResult> {
    return this.options.history.exclusive(async (): Promise<ChangeResult> => {
      const entry = await this.inBook(chapterId);
      if (entry.kind !== 'live') return entry;
      const all = await this.live(entry.entry.bookId);
      const current = all.findIndex((other) => other.id === chapterId);
      const live = all.filter((other) => other.id !== chapterId);
      const slot = before === null ? live.length : live.findIndex((other) => other.id === before);
      if (before === chapterId || slot === current) return { kind: 'done' };
      if (slot < 0) return { kind: 'missing' };
      const { placed, moved } = place(live, slot, [entry.entry]);
      return done(await this.options.write([...movedOps(placed), ...movedOps(moved)]));
    });
  }

  /** Replaces the given fields of a live chapter's entry (B6). */
  update(chapterId: string, fields: EntryFields): Promise<ChangeResult> {
    return this.options.history.exclusive(async (): Promise<ChangeResult> => {
      const entry = await this.inBook(chapterId);
      if (entry.kind !== 'live') return entry;
      const { title = entry.entry.title, synopsis = entry.entry.synopsis } = fields;
      return done(await this.options.write([{ kind: 'put', store: 'entries', value: { ...entry.entry, title, synopsis } }]));
    });
  }

  /** Moves a live chapter to the trash, noting when and on this device; nothing else changes (L6, B7). */
  trash(chapterId: string): Promise<ChangeResult> {
    return this.options.history.exclusive(async (): Promise<ChangeResult> => {
      const entry = await this.inBook(chapterId);
      if (entry.kind !== 'live') return entry;
      const trashed = { at: this.options.now(), device: this.options.device };
      return done(await this.options.write([{ kind: 'put', store: 'entries', value: { ...entry.entry, trashed } }]));
    });
  }

  /** Brings a chapter back from the trash where its position puts it (L4, B8, B9). */
  restore(chapterId: string): Promise<TrashResult> {
    return this.options.history.exclusive(async (): Promise<TrashResult> => {
      const entry = await this.options.store.read('entries', chapterId);
      if (entry === undefined) return { kind: 'missing' };
      if (entry.trashed === null) return { kind: 'live' };
      return done(await this.options.write([{ kind: 'put', store: 'entries', value: { ...entry, trashed: null } }]));
    });
  }

  /**
   * Deletes a chapter in the trash forever: its entry, text, every owner's snapshots and their texts, its conflict
   * copies and its notes, tombstones too (part 19e, DL1′), in one write (B10). Never a live chapter (B11).
   */
  deleteForever(chapterId: string): Promise<TrashResult> {
    return this.options.history.exclusive(async (): Promise<TrashResult> => {
      const entry = await this.options.store.read('entries', chapterId);
      if (entry === undefined) return { kind: 'missing' };
      if (entry.trashed === null) return { kind: 'live' };
      return done(await this.options.write(await this.purge([entry])));
    });
  }

  /** Deletes every chapter in the book's trash forever, as {@link deleteForever} does, in one write (B10). */
  emptyTrash(bookId: string): Promise<{ readonly kind: 'done' } | NotWritten> {
    return this.options.history.exclusive(async () => {
      const { records } = await this.options.store.readWhere('entries', 'bookId', bookId);
      return done(await this.options.write(await this.purge(records.filter((entry) => entry.trashed !== null))));
    });
  }

  /**
   * Restores a live chapter from one of its snapshots (L4, B12, B13): snapshots `current` (the text the editor holds,
   * which it must stop changing until this settles) as `before-restore`, then writes the snapshot's text as the
   * chapter's, if it is still live. The caller loads the returned text into the editor.
   */
  async restoreSnapshot(current: ChapterText, snapshotId: string): Promise<RestoreSnapshotResult> {
    const { history, store } = this.options;
    const checked = await history.exclusive(async (): Promise<Missing | NotWritten | { readonly kind: 'ok' }> => {
      const entry = await this.inBook(current.id, current.bookId);
      if (entry.kind !== 'live') return entry;
      const snapshot = await store.read('snapshots', snapshotId);
      return snapshot?.chapterId === current.id ? { kind: 'ok' } : { kind: 'missing' };
    });
    if (checked.kind !== 'ok') return checked;
    const restored = await history.restore(current, snapshotId);
    if (restored.kind !== 'restored') return restored;
    return history.exclusive(async (): Promise<RestoreSnapshotResult> => {
      const entry = await this.inBook(current.id, current.bookId);
      if (entry.kind !== 'live') return entry;
      const written = await this.options.write([{ kind: 'put', store: 'chapters', value: { ...current, text: restored.text } }]);
      return written.kind === 'committed' ? restored : written;
    });
  }

  /**
   * Splits a live chapter at `at`, starts of its split points (L7, B14, B15): `current` (the text the editor holds)
   * keeps the first piece, and each other piece becomes a new chapter right after it, in order, all in one write.
   */
  split(current: ChapterText, at: readonly number[]): Promise<SplitResult> {
    return this.options.history.exclusive(async (): Promise<SplitResult> => {
      const entry = await this.inBook(current.id, current.bookId);
      if (entry.kind !== 'live') return entry;
      let pieces: Piece[];
      try {
        pieces = splitChapter(parseChapter(current.text), at);
      } catch (error) {
        if (error instanceof SplitError) return { kind: 'refused', error };
        throw error;
      }
      const paired = await this.withIds(pieces.slice(1));
      if (paired === null) return { kind: 'failed', error: new Error('no free chapter ID') };
      const live = await this.live(current.bookId);
      const { placed, moved } = place(live, live.findIndex((other) => other.id === current.id) + 1, paired);
      const chapters = placed.map(([[piece, id], position]): readonly [ChapterEntry, string] => [
        { id, bookId: current.bookId, title: piece.title ?? entry.entry.title, position, synopsis: '', trashed: null },
        piece.text,
      ]);
      const written = await this.options.write([
        ...pieces.slice(0, 1).map((piece): WriteOp => ({ kind: 'put', store: 'chapters', value: { ...current, text: piece.text } })),
        ...newOps(chapters),
        ...movedOps(moved),
      ]);
      return written.kind === 'committed' ? { kind: 'split', chapters: chapters.map(([chapter]) => chapter) } : written;
    });
  }

  /**
   * Replace-all (L3; part 19a, table L, principle 5): snapshots each chapter's text as read (`before`) as
   * `before-replace-all` (L4), then writes every `after` in one write, which happens only while every chapter is live and
   * its stored text is still its `before` (L7). A snapshot not written writes no text (L5). The caller has saved and
   * closed the open chapter, so nothing types into a text being replaced.
   */
  async replaceAll(chapters: readonly ReplacedText[]): Promise<ReplaceAllResult> {
    const { history } = this.options;
    const current = (): Promise<boolean> => this.unchanged(chapters);
    const checked = await history.exclusive(current);
    if (checked !== true) return checked === false ? { kind: 'changed' } : checked;
    for (const chapter of chapters) {
      const taken = await history.take({ id: chapter.id, bookId: chapter.bookId, text: chapter.before }, 'before-replace-all');
      if (taken.kind === 'stale' || taken.kind === 'failed') return taken;
    }
    return history.exclusive(async (): Promise<ReplaceAllResult> => {
      if (!(await current())) return { kind: 'changed' };
      const written = await this.options.write(chapters.map((chapter): WriteOp => ({ kind: 'put', store: 'chapters', value: { id: chapter.id, bookId: chapter.bookId, text: chapter.after } })));
      return written.kind === 'committed' ? { kind: 'replaced' } : written;
    });
  }

  /**
   * The book-wide undo of a replace-all (L3, L4; part 19a, table U): each chapter whose stored text is still its `after`,
   * in the book or its trash, is snapshotted as `before-restore` and given its `before` back, all in one write; a chapter
   * changed since or deleted forever keeps what it has. A snapshot or the write not written restores nothing (U5).
   */
  async undoReplaceAll(chapters: readonly ReplacedText[]): Promise<UndoReplaceAllResult> {
    const { history, store } = this.options;
    const holding = (list: readonly ReplacedText[]): Promise<ReplacedText[]> =>
      Promise.all(list.map(async (chapter) => ((await store.read('chapters', chapter.id))?.text === chapter.after ? [chapter] : []))).then((found) => found.flat());
    const undone = await history.exclusive(() => holding(chapters));
    if (!Array.isArray(undone)) return undone;
    for (const chapter of undone) {
      const taken = await history.take({ id: chapter.id, bookId: chapter.bookId, text: chapter.after }, 'before-restore');
      if (taken.kind === 'stale' || taken.kind === 'failed') return taken;
    }
    return history.exclusive(async (): Promise<UndoReplaceAllResult> => {
      const still = await holding(undone);
      const written = await this.options.write(still.map((chapter): WriteOp => ({ kind: 'put', store: 'chapters', value: { id: chapter.id, bookId: chapter.bookId, text: chapter.before } })));
      if (written.kind !== 'committed') return written;
      const restored = new Set(still.map((chapter) => chapter.id));
      return { kind: 'undone', restored: chapters.filter((c) => restored.has(c.id)).map((c) => c.id), kept: chapters.filter((c) => !restored.has(c.id)).map((c) => c.id) };
    });
  }

  /**
   * A re-import (L8, L9; part 23b, table AP, principle 1): each taken chapter's reviewed text snapshotted as
   * `before-reimport` (AP3), then in one write each taken text and title, a conflict copy of each reviewed text asked for,
   * and each new chapter after its anchor, its neighbours moved as `place` needs (AP4); written only while every taken
   * chapter is live in `bookId` with its stored text the reviewed one and every anchor live, checked before the snapshots
   * and again in the write's turn (AP1, AP2). The caller has saved and closed the open chapter (AP6).
   *
   * Part 23c, table AC: in the same write, each Word comment of a taken, added or `commented` chapter whose ID no record
   * holds, readable or refused (AC1, AC2, C6); a commented chapter must be live with its stored text the one its comments
   * were anchored in (AC4).
   */
  async reimport(bookId: string, taken: readonly ReimportedText[], added: readonly ReimportedDraft[], commented: readonly CommentedChapter[] = []): Promise<ReimportResult> {
    const { history, store } = this.options;
    const current = async (): Promise<boolean> => {
      for (const chapter of taken) {
        // AP2: live in the book, its text as reviewed, and its title too when the file's is taken (deep-fix r4).
        const entry = await this.inBook(chapter.id, bookId);
        if (entry.kind !== 'live' || (chapter.title !== null && entry.entry.title !== chapter.titleBefore)) return false;
        if ((await store.read('chapters', chapter.id))?.text !== chapter.before) return false;
      }
      for (const draft of added) if (draft.after !== null && (await this.inBook(draft.after, bookId)).kind !== 'live') return false;
      // AC4: a commented chapter as its comments were anchored in.
      for (const chapter of commented) {
        if ((await this.inBook(chapter.id, bookId)).kind !== 'live' || (await store.read('chapters', chapter.id))?.text !== chapter.text) return false;
      }
      return true;
    };
    const checked = await history.exclusive(current);
    if (checked !== true) return checked === false ? { kind: 'changed' } : checked;
    for (const chapter of taken) {
      const snapshot = await history.take({ id: chapter.id, bookId, text: chapter.before }, 'before-reimport');
      if (snapshot.kind === 'stale' || snapshot.kind === 'failed') return snapshot;
    }
    return history.exclusive(async (): Promise<ReimportResult> => {
      if (!(await current())) return { kind: 'changed' };
      const copies = await this.withCopyIds(taken.filter((chapter) => chapter.copy));
      const paired = await this.withIds(added);
      if (copies === null || paired === null) return { kind: 'failed', error: new Error('no free ID') };
      // CS7b: each run of new chapters after one anchor placed there in order, the book's order and positions updated after each.
      const runs: { readonly after: string | null; readonly items: [ReimportedDraft, string][] }[] = [];
      for (const pair of paired) {
        const last = runs.at(-1);
        if (last?.after === pair[0].after) last.items.push(pair);
        else runs.push({ after: pair[0].after, items: [pair] });
      }
      let live = await this.live(bookId);
      const entries = new Map<string, ChapterEntry>();
      const made: (readonly [ChapterEntry, string, ReimportedDraft])[] = [];
      for (const run of runs) {
        const { placed, moved } = place(live, run.after === null ? 0 : live.findIndex((entry) => entry.id === run.after) + 1, run.items);
        for (const [entry, position] of moved) entries.set(entry.id, { ...entry, position });
        const fresh: ChapterEntry[] = [];
        for (const [[draft, id], position] of placed) {
          const entry: ChapterEntry = { id, bookId, title: draft.title, position, synopsis: '', trashed: null };
          entries.set(id, entry);
          fresh.push(entry);
          made.push([entry, draft.text, draft]);
        }
        live = orderChapters([...live.map((other) => entries.get(other.id) ?? other), ...fresh]);
      }
      // CS8: a taken chapter's new title.
      const titles = new Map(taken.flatMap((chapter) => (chapter.title === null ? [] : [[chapter.id, chapter.title] as const])));
      for (const entry of live) {
        const title = titles.get(entry.id);
        if (title !== undefined) entries.set(entry.id, { ...entry, title });
      }
      const at = this.options.now();
      // AC1, AC2: each comment once, under an ID no note holds, readable or refused (CR3), a tombstone too (part 19e,
      // principle 7), nor a comment record left from before the move (MG14), in any book; the ID is its chapter's (table DD,
      // deep-fix r3), so a comment on a chapter taken anew never meets one kept in the trash.
      const comments: NoteRecord[] = [];
      const held = new Set<string>();
      const owners: (readonly [string, readonly CommentDraft[] | undefined])[] = [
        ...taken.map((chapter) => [chapter.id, chapter.comments] as const),
        ...made.map(([entry, , draft]) => [entry.id, draft.comments] as const),
        ...commented.map((chapter) => [chapter.id, chapter.comments] as const),
      ];
      for (const [chapterId, drafts] of owners) {
        for (const draft of drafts ?? []) {
          const id = await commentId(this.options.digest, bookId, chapterId, draft.sourceKey);
          if (held.has(id)) continue;
          held.add(id);
          // CR3: a record its reader refuses holds the ID as much as a readable one; any other failure fails the write.
          const holds = (found: Promise<unknown>): Promise<boolean> =>
            found.then(
              (record) => record !== undefined,
              (error: unknown) => {
                if (error instanceof StoreRecordError) return true;
                throw error;
              },
            );
          const stored = (await holds(store.read('notes', id))) || (await holds(store.read('comments', id)));
          if (!stored) comments.push(commentNote(id, { ...draft, id, bookId, chapterId, storedAt: at }));
        }
      }
      const written = await this.options.write([
        ...taken.map((chapter): WriteOp => ({ kind: 'put', store: 'chapters', value: { id: chapter.id, bookId, text: chapter.after } })),
        ...copies.map(([chapter, id]): WriteOp => ({ kind: 'put', store: 'conflictCopies', value: { id, chapterId: chapter.id, bookId, text: chapter.before, createdAt: at, origin: 'docx-reimport' } })),
        ...[...entries.values()].map((entry): WriteOp => ({ kind: 'put', store: 'entries', value: entry })),
        ...made.map(([entry, text]): WriteOp => ({ kind: 'put', store: 'chapters', value: { id: entry.id, bookId, text } })),
        ...comments.map((note): WriteOp => ({ kind: 'put', store: 'notes', value: note })),
      ]);
      return written.kind === 'committed' ? { kind: 'reimported', added: made.map(([entry]) => entry), comments: comments.length } : written;
    });
  }

  /** Each of `items` with a fresh ID no conflict copy has and no other item got, or null when one ran out of tries (as {@link withIds}). */
  private async withCopyIds<T>(items: readonly T[]): Promise<[T, string][] | null> {
    const out: [T, string][] = [];
    for (const item of items) {
      let id: string | undefined;
      for (let tries = 0; id === undefined && tries < MAX_ID_TRIES; tries++) {
        const candidate = this.options.newId();
        if (!out.some(([, other]) => other === candidate) && (await this.options.store.read('conflictCopies', candidate)) === undefined) id = candidate;
      }
      if (id === undefined) return null;
      out.push([item, id]);
    }
    return out;
  }

  /** L7: whether every chapter of `chapters` is live in its book and its stored text is still its `before`. */
  private async unchanged(chapters: readonly ReplacedText[]): Promise<boolean> {
    for (const chapter of chapters) {
      if ((await this.inBook(chapter.id, chapter.bookId)).kind !== 'live') return false;
      if ((await this.options.store.read('chapters', chapter.id))?.text !== chapter.before) return false;
    }
    return true;
  }

  /** The live chapters of a book, in order. */
  private async live(bookId: string): Promise<ChapterEntry[]> {
    const { records } = await this.options.store.readWhere('entries', 'bookId', bookId);
    return orderChapters(records.filter((entry) => entry.trashed === null));
  }

  /** The chapter's entry if it is live (and in `bookId`, when given); otherwise why not. */
  private async inBook(chapterId: string, bookId?: string): Promise<Missing | { readonly kind: 'live'; readonly entry: ChapterEntry }> {
    const entry = await this.options.store.read('entries', chapterId);
    if (entry === undefined || (bookId !== undefined && entry.bookId !== bookId)) return { kind: 'missing' };
    if (entry.trashed !== null) return { kind: 'trashed' };
    return { kind: 'live', entry };
  }

  /**
   * Each of `items` with a fresh ID that no entry or text has and no other item got, or null when one ran out of tries
   * (B2).
   */
  private async withIds<T>(items: readonly T[]): Promise<[T, string][] | null> {
    const out: [T, string][] = [];
    const taken = new Set<string>();
    for (const item of items) {
      let id: string | undefined;
      for (let tries = 0; id === undefined && tries < MAX_ID_TRIES; tries++) {
        const candidate = this.options.newId();
        const free = !taken.has(candidate) && (await this.options.store.read('entries', candidate)) === undefined && (await this.options.store.read('chapters', candidate)) === undefined;
        if (free) id = candidate;
      }
      if (id === undefined) return null;
      taken.add(id);
      out.push([item, id]);
    }
    return out;
  }

  /** The ops that delete `entries` forever, with their texts, snapshots and conflict copies (B10). */
  private async purge(entries: readonly ChapterEntry[]): Promise<WriteOp[]> {
    const { store } = this.options;
    const perEntry = await Promise.all(
      entries.map(async (entry): Promise<WriteOp[]> => {
        const snapshots = await store.readWhere('snapshots', 'chapterId', entry.id);
        const copies = await store.readWhere('conflictCopies', 'bookId', entry.bookId);
        const comments = await store.readWhere('comments', 'chapterId', entry.id);
        const notes = await store.readWhere('notes', 'chapterId', entry.id);
        // A snapshot or comment record its reader refuses still goes (with a snapshot's text) by the key it is stored under.
        const keys = (found: { readonly records: readonly { readonly id: string }[]; readonly invalid: readonly InvalidRecord[] }): string[] => [
          ...found.records.map((record) => record.id),
          ...found.invalid.map((record) => record.key).filter((key) => typeof key === 'string'),
        ];
        const ids = keys(snapshots);
        return [
          { kind: 'delete', store: 'entries', id: entry.id },
          { kind: 'delete', store: 'chapters', id: entry.id },
          ...ids.flatMap((id): WriteOp[] => [
            { kind: 'delete', store: 'snapshots', id },
            { kind: 'delete', store: 'snapshotTexts', id },
          ]),
          ...copies.records.filter((copy) => copy.chapterId === entry.id).map((copy): WriteOp => ({ kind: 'delete', store: 'conflictCopies', id: copy.id })),
          // Part 19e, DL1′: its notes, tombstones and refused ones too, and what is left of its comment records (MG14).
          ...keys(notes).map((id): WriteOp => ({ kind: 'delete', store: 'notes', id })),
          ...keys(comments).map((id): WriteOp => ({ kind: 'delete', store: 'comments', id })),
        ];
      }),
    );
    return perEntry.flat();
  }
}
